import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { TenantLogo } from '@/components/shell/TenantLogo';

import { useAuth } from './useAuth';

/**
 * Branded dead-end for an authenticated user whose effective permission set is
 * empty (PTV2-1413).
 *
 * A user can now be provisioned with ZERO roles — JIT with no default roles, or
 * every claim role unmapped-and-dropped (contract §4.1). In that state `/me`
 * still answers but nothing requiring `view` is permitted, so the normal app
 * shell would only render empty screens and fire denied queries. `SessionGuard`
 * short-circuits to this page instead, above the router and the feature-flag
 * probe, so the only request in flight is the `/me` profile fetch.
 *
 * The only affordance offered is sign-out — there is nothing this user can do
 * here, so we route them back out rather than into a broken shell.
 */
export function NoPermissionsPage(): ReactElement {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  return (
    <main
      role="alert"
      className="flex min-h-screen items-center justify-center bg-muted/30 p-6"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-4 flex items-center gap-2.5">
            <TenantLogo />
            <span className="text-lg font-semibold tracking-tight text-brand">
              {t('shell.rail.brandName')}
            </span>
          </div>
          <CardTitle>{t('auth.noPermissions.heading')}</CardTitle>
          <CardDescription>{t('auth.noPermissions.message')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              void signOut();
            }}
          >
            {t('auth.noPermissions.signOut')}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
